import { type FormHTMLAttributes, ReactNode } from 'react'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { WithWrapper } from '@ui/components/WithWrapper'
import type { FormSubmitHandler } from '@ui/features/forms'
import { Responsive } from '@ui/features/themes/basic-theme'
import { SizesAndSpaces } from '@ui/features/themes/design/1_sizes_spaces'
import { Cross2Icon } from '@ui/icons/Cross2Icon'
import type { SxProps } from '@ui/lib/mui'

const {
  Width: { modal: modalWidth },
  Height: { modal: modalHeight },
} = SizesAndSpaces

type ModalFormProps = Omit<FormHTMLAttributes<HTMLFormElement>, 'children' | 'onSubmit'> & {
  onSubmit?: FormSubmitHandler
}

type ModalDialogProps = {
  /** Content of the modal dialog */
  children: ReactNode

  /** Title of the modal dialog */
  title: string

  /** Whether the modal dialog is open */
  open: boolean

  /**
   * Callback function when the modal dialog is closed
   * If this is not provided, the modal cannot be closed by clicking outside or pressing Escape. No close button will be shown.
   **/
  onClose?: () => void

  /**
   * Callback function when the modal dialog has finished its close transition.
   * This is a good moment to stop rendering the modal to improve performance.
   * If the modal stops being rendered as soon as it is closed, the transition cannot be seen.
   **/
  onTransitionExited?: () => void

  /**
   * Optional action element to display before the title.
   * Usually an icon button to perform an action related to the modal, like going back.
   **/
  titleAction?: ReactNode

  /**
   * Optional footer element to display at the bottom of the modal.
   * Usually contains action buttons.
   **/
  footer?: ReactNode

  /** If true, the modal will have a compact height instead of filling most of the screen */
  compact?: boolean

  /** When you want to set the max height of the modal to be smaller than 100dvh */
  maxHeight?: string | Partial<Responsive>

  /** Custom styles for the modal dialog */
  sx?: SxProps

  /** Predefined width for the modal dialog */
  width?: keyof typeof modalWidth

  /**
   * When a form is used inside the modal, we need to wrap the contents of the modal with a form element,
   * so that we can keep the form submission in the footer.
   */
  formProps?: ModalFormProps

  /** Optional test id for the dialog root */
  testId?: string
}

/**
 * When we have a form inside the modal, we can use this component to wrap it with a form element,
 * so that we can keep the form submission in the footer.
 */
const Form = ({ onSubmit, ...props }: ModalFormProps) => (
  <form
    {...props}
    onSubmit={
      onSubmit
        ? event => {
            event.stopPropagation()
            void onSubmit(event)
          }
        : undefined
    }
  />
)

export const ModalDialog = ({
  children,
  open,
  onClose,
  onTransitionExited,
  title,
  titleAction,
  footer,
  compact,
  maxHeight = { mobile: modalHeight.sm, desktop: modalHeight.md },
  width = 'md',
  sx,
  testId,
  formProps,
}: ModalDialogProps) => (
  <Dialog
    open={open}
    onClose={onClose}
    onTransitionExited={onTransitionExited}
    disableRestoreFocus
    data-testid={testId}
    sx={{
      '& .MuiDialog-paper': {
        maxWidth: '100dvw',
        ...(compact
          ? // In compact mode height grows dynamically, otherwise it's fixed to maxHeight
            // Compact mode still needs maxHeight constraint to prevent overflow
            { height: 'auto', maxHeight, '& .MuiCard-root': { height: 'auto' } }
          : { height: maxHeight, '& .MuiCard-root': { height: '100%' } }),
      },
      ...sx,
    }}
  >
    <Card sx={{ width: { tablet: modalWidth[width], mobile: '100dvw' }, display: 'flex', flexDirection: 'column' }}>
      <WithWrapper shouldWrap={formProps} Wrapper={Form} {...formProps}>
        <CardHeader
          size="medium"
          variant="modal"
          action={
            onClose && (
              <IconButton onClick={onClose} size="extraSmall">
                <Cross2Icon />
              </IconButton>
            )
          }
          avatar={titleAction}
          title={
            <Typography variant="headingXsBold" color="textSecondary">
              {title}
            </Typography>
          }
        />
        <CardContent sx={{ flexGrow: 1, overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {children}
        </CardContent>
        {footer && <CardActions>{footer}</CardActions>}
      </WithWrapper>
    </Card>
  </Dialog>
)
